import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStoreActions } from 'easy-peasy';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// icons
import DeleteIcon from '../../../assets/images/svg/delete-icon.svg';
import VectorIcon from '../../../assets/images/svg/vector.svg';

// tools
import { _filterMaterialData } from '../../tools/manager';

// components
import Modal from '../../components/Modal_3';
import Table from '../Table';

/**
 * ✅ Nouveau design "APRÈS" (portail admin)
 * - On SUPPRIME la liste des matériaux / références (catalogue).
 * - On garde un shape compatible MaterialData (name, address, quantity, quantityLabel, description...)
 * - Champs par ligne: designation, lieu, quantité, unité
 * - Champs entête: client, devis/bdc (ctm), nature de l'intervention (globale)
 */

// ligne vide (shape "MaterialData-like")
const refMaterial = {
  // MaterialData fields (compat)
  name: '', // designation
  address: '', // lieu
  quantity: '', // string côté input, cast en Float à l’envoi
  quantityLabel: '', // ex: "10 U" / "250 ml"
  description: '', // on y met la "nature de l’intervention" globale (utile pour compat / lecture)
  surface: '',
  poseAddress: '',
  referenceQr: '',
  indexRef: null,

  // UI only
  unit: 'U', // "U" ou "ml"
};

const UNIT_OPTIONS = [
  { value: 'U', label: 'U' },
  { value: 'ml', label: 'ml' },
];

const PrefillOrder = () => {
  const navigate = useNavigate();
  const { idCustomer, idWorksite } = useParams();

  // state
  const [customer, setCustomer] = useState();
  const [bcCtm, setBcCtm] = useState(''); // DEVIS / BDC (N°)
  const [natureIntervention, setNatureIntervention] = useState(''); // champ global (APRÈS)
  const [listeMaterialCreate, setListeMaterialCreate] = useState([{ ...refMaterial }]);
  const [formError, setFormError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [numeroBon, setNumeroBon] = useState(null);

  // store actions
  const getCompany = useStoreActions(actions => actions.middleware.CompanyMiddleware.getCompany);
  const getNextCounterValue = useStoreActions(
    actions => actions.middleware.CouponPreInterMiddleware.getNextCounterValue
  );
  const createPreInterv = useStoreActions(
    actions => actions.middleware.CouponPreInterMiddleware.createPreInterv
  );

  // ====== API CALLS ======

  const callGetCompany = async () => {
    try {
      const res = await getCompany(idCustomer);
      setCustomer(res);
    } catch (error) {
      console.log('Error: ', error);
      window.location.href = '/clients';
    }
  };

  const callCreatePreInterventionCoupon = async data => {
    try {
      await createPreInterv(data);
      setShowModal(true);
    } catch (error) {
      console.log('Error: ', error);
      toast.error('❌ #2 Une erreur est survenue lors de la création du bon');
    }
  };

  const callLastNumberCoupon = async data => {
    try {
      const res = await getNextCounterValue('couponPreInter'); // ex: "124"
      const newData = { ...data, numero: res };
      setNumeroBon(res);
      await callCreatePreInterventionCoupon(newData);
    } catch (error) {
      console.log('Error: ', error);
      toast.error('❌ #1 Une erreur est survenue lors de la création du bon');
    }
  };

  // ====== HELPERS ======

  const normalizeLineForSave = (line, index) => {
    const qty = typeof line.quantity === 'string' ? line.quantity.replace(',', '.') : line.quantity;
    const numericQty = qty === '' || qty === null || qty === undefined ? null : Number(qty);

    const safeUnit = line.unit?.trim() || 'U';

    const quantityLabel =
      numericQty && numericQty > 0 ? `${numericQty} ${safeUnit}`.trim() : (line.quantityLabel || '');

    const address = (line.address || '').trim();
    const description = (natureIntervention || line.description || '').trim();

    return {
      indexRef: typeof line.indexRef === 'number' ? line.indexRef : index,

      // ✅ champs "APRÈS"
      name: (line.name || '').trim(),
      address,
      quantity: numericQty,
      quantityLabel,
      description,

      // ✅ compat: on duplique aussi dans poseAddress (souvent utilisé ailleurs)
      poseAddress: address,

      // optionnels
      surface: (line.surface || '').trim(),
      referenceQr: (line.referenceQr || '').trim() || undefined,
    };
  };

  // ====== FORM LOGIC ======

  const addMaterial = e => {
    e.preventDefault();
    setListeMaterialCreate(prev => [...prev, { ...refMaterial, description: natureIntervention }]);
  };

  const deleteMaterial = (e, index) => {
    e.preventDefault();
    setListeMaterialCreate(prev => prev.filter((_, i) => i !== index));
  };

  const changeValueMaterial = (index, key, value) => {
    setListeMaterialCreate(prev => {
      const copy = prev.map(item => ({ ...item }));
      const line = copy[index];
      if (!line) return prev;

      line[key] = value;

      // auto-sync quantityLabel à chaque changement qty/unit
      if (key === 'quantity' || key === 'unit') {
        const qtyStr = String(line.quantity ?? '').replace(',', '.');
        const qtyNum = qtyStr === '' ? null : Number(qtyStr);
        if (qtyNum && qtyNum > 0) {
          line.quantityLabel = `${qtyNum} ${line.unit || 'U'}`.trim();
        } else {
          line.quantityLabel = '';
        }
      }

      copy[index] = line;
      return copy;
    });
  };

  const resetFormError = () => setFormError('');

  const isOkForm = () => {
    if (!bcCtm.trim()) {
      setFormError('Entrer un numéro DEVIS / BDC (N°)');
      return false;
    }
    if (!listeMaterialCreate.length) {
      setFormError('Ajouter au moins une ligne.');
      return false;
    }

    for (const element of listeMaterialCreate) {
      // APRÈS: designation + quantité + unité requis
      if (!element.name?.trim()) {
        setFormError('Défaut de désignation.');
        return false;
      }

      const qtyStr = String(element.quantity ?? '').replace(',', '.');
      const qty = qtyStr === '' ? NaN : Number(qtyStr);
      if (!Number.isFinite(qty) || qty <= 0) {
        setFormError('Quantité invalide (doit être un chiffre > 0).');
        return false;
      }

      if (!element.unit?.trim()) {
        setFormError("Défaut d'unité (ml ou U).");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = e => {
    e.preventDefault();

    if (!isOkForm()) return;

    const normalizedMaterials = listeMaterialCreate.map(normalizeLineForSave);

    console.log('normalized BEFORE filter', normalizedMaterials);
    const filtered = _filterMaterialData(normalizedMaterials);
    console.log('normalized AFTER filter', filtered);

    const newdata = {
      ctm: bcCtm.trim(),
      couponPreInterWorksitesId: idWorksite,
      // on conserve ton pipeline: _filterMaterialData + JSON.stringify dans middleware
      materials: _filterMaterialData(normalizedMaterials),
    };

    callLastNumberCoupon(newdata);
  };

  const goBack = () => {
    setShowModal(false);
    navigate(-1);
  };

  // ====== EFFECTS ======

  useEffect(() => {
    resetFormError();
  }, [bcCtm, natureIntervention, listeMaterialCreate]);

  useEffect(() => {
    callGetCompany();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ====== RENDER ======

  return (
    <section className="pt-14">
      <form onSubmit={handleSubmit}>
        {/* Entête (APRÈS) */}
        <div className="flex flex-col lg:flex-row lg:space-x-8 mx-14">
          {/* Nom du client */}
          <div className="flex flex-col space-y-4">
            <label htmlFor="client">Nom du CLIENT</label>
            <p className="w-fit rounded-lg placeholder:text-blue-admin p-4 border border-white-admin">
              {customer?.name}
            </p>
          </div>

          {/* DEVIS / BDC */}
          <div className="flex flex-col space-y-4">
            <label htmlFor="bcCtm">DEVIS / BDC</label>
            <input
              id="bcCtm"
              className="w-[214px] rounded-lg placeholder:text-blue-admin py-4 px-3 border border-white-admin input"
              type="text"
              placeholder="N°"
              value={bcCtm}
              onChange={e => setBcCtm(e.target.value)}
            />
          </div>

          {/* Nature de l’intervention (globale) */}
          <div className="flex flex-col space-y-4">
            <label htmlFor="natureInter">Nature de l’intervention</label>
            <input
              id="natureInter"
              className="w-[260px] rounded-lg placeholder:text-blue-admin py-4 px-3 border border-white-admin input"
              type="text"
              placeholder="Nature de l’intervention"
              value={natureIntervention}
              onChange={e => setNatureIntervention(e.target.value)}
            />
          </div>
        </div>

        {/* Tableau (APRÈS) : designation / lieu / quantite / unité */}
        <div className="mt-10 mx-14 overflow-x-auto">
          <div className="flex items-center justify-between mb-4">
            <h5 className="font-semibold">Etat des lieux</h5>
          </div>

          {!!listeMaterialCreate.length && (
            <Table>
              <thead>
                <tr>
                  <th>Désignation</th>
                  <th>Lieu</th>
                  <th>Quantité</th>
                  <th>Unité</th>
                  <th></th>
                </tr>
              </thead>

              <tbody>
                {listeMaterialCreate.map((line, index) => (
                  <tr key={index}>
                    {/* designation -> MaterialData.name */}
                    <td className="border border-white border-r-8">
                      <input
                        className="mt-3 w-[320px] rounded-lg placeholder:text-blue-admin py-4 px-3 border border-white-admin text-blue-admin inputFocus"
                        type="text"
                        placeholder="Désignation"
                        value={line.name}
                        onChange={e => changeValueMaterial(index, 'name', e.target.value)}
                      />
                    </td>

                    {/* lieu -> MaterialData.address (optionnel, si vide = ok) */}
                    <td className="border border-white border-x-8">
                      <input
                        className="mt-3 w-[360px] rounded-lg placeholder:text-blue-admin py-4 px-3 border border-white-admin text-blue-admin input"
                        type="text"
                        placeholder="Lieu de l’intervention (optionnel)"
                        value={line.address}
                        onChange={e => changeValueMaterial(index, 'address', e.target.value)}
                      />
                    </td>

                    {/* quantité -> MaterialData.quantity */}
                    <td className="border border-white border-x-8">
                      <input
                        className="mt-3 w-[160px] rounded-lg placeholder:text-blue-admin py-4 px-3 border border-white-admin text-blue-admin input"
                        type="number"
                        inputMode="decimal"
                        placeholder="Quantité"
                        value={line.quantity}
                        onChange={e => changeValueMaterial(index, 'quantity', e.target.value)}
                        min="0"
                        step="0.01"
                      />
                    </td>

                    {/* unité -> stockée via quantityLabel */}
                    <td className="border border-white border-x-8">
                      <select
                        className="mt-3 w-[160px] rounded-lg py-4 px-3 border border-white-admin text-blue-admin input"
                        value={line.unit}
                        onChange={e => changeValueMaterial(index, 'unit', e.target.value)}
                      >
                        {UNIT_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Delete */}
                    <td className="text-center border border-white border-x-8">
                      <button
                        type="button"
                        className="w-8 p-2 border rounded-lg border-white-admin"
                        onClick={e => deleteMaterial(e, index)}
                        title="Supprimer la ligne"
                      >
                        <img src={DeleteIcon} alt="delete icon" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <div className="mt-5">
            <button
              type="button"
              className="w-[280px] py-3 text-sm text-blue-admin rounded-lg border border-white-admin mb-2"
              onClick={addMaterial}
            >
              + Ajouter une nouvelle intervention
            </button>
          </div>
        </div>

        {!!formError && <p className="text-red-error text-end mt-5 mr-20 italic">{formError}</p>}

        {/* Footer actions */}
        <div className="mt-16 py-4 flex justify-between items-center border-t border-white-admin">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="border rounded-lg cursor-pointer ml-14 border-gray-admin px-5 py-3 text-blue-admin"
          >
            Annuler
          </button>

          <div className="cursor-pointer mr-14 bg-yellow-admin rounded-lg">
            <button type="submit" className="py-3 px-5 text-blue-admin">
              Enregistrer ce bon pré-rempli
            </button>
          </div>
        </div>
      </form>

      <Modal
        showModal={showModal}
        title={`Bon d'intervention n°${numeroBon}`}
        text="Le bon d'intervention pré rempli est bien enregistré !"
        image={VectorIcon}
      >
        <button className="px-4 py-3 text-white rounded-lg cursor-pointer bg-blue-admin w-full" onClick={goBack}>
          J'ai compris
        </button>
      </Modal>
    </section>
  );
};

export default PrefillOrder;